import React, { useState, useEffect, useRef } from "react";
import Lightbox from "yet-another-react-lightbox";
import Zoom from "yet-another-react-lightbox/plugins/zoom";
import Inline from "yet-another-react-lightbox/plugins/inline";
import FullScreen from "yet-another-react-lightbox/plugins/fullscreen";
import Download from "yet-another-react-lightbox/plugins/download";
import "yet-another-react-lightbox/styles.css";

import { Task } from "@induz/sdk";
import "./imageViewer.scss";
import { useToast } from "components/Toaster/index";
import useTheme from "hooks/observer";
import heic2any from "heic2any";

// Assets
import logo from "assets/images/homepage/main/logo.svg";
import logoDark from "assets/images/homepage/main/logoDark.svg";
import downloadIcon from "assets/images/pdf_viewer/download_icon_for_pdf.svg";
import downloadIconDark from "assets/images/pdf_viewer/download_icon_for_pdf_dark_mode.svg";
const fullscreenIcon = null; // Replaced by SVG components
const fullscreenIconDark = null; // Replaced by SVG components

const EnterFullScreenIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M15 3h6v6M9 21H3v-6M21 15v6h-6M3 9V3h6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ExitFullScreenIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M21 9h-6V3M3 15h6v6M15 21v-6h6M9 3v6H3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

// Icon Components
const CloseIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
        <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const PlusIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M12 5v14M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const MinusIcon = () => (
    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M5 12h14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
);

const ImageViewer = ({ isVisible, handleCancel, fo, fileName, dupCurrentPath }) => {
    const [loading, setLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState(null);
    const [error, setError] = useState(null);
    const [retryTrigger, setRetryTrigger] = useState(0);
    const [isFullscreen, setIsFullscreen] = useState(false);
    const showToast = useToast();
    const zoomRef = useRef(null);
    const theme = useTheme();
    const fullScreenRef = useRef(null);

    // Sync fullscreen state
    useEffect(() => {
        const handleFSChange = () => {
            setIsFullscreen(!!document.fullscreenElement);
        };
        document.addEventListener("fullscreenchange", handleFSChange);
        return () => document.removeEventListener("fullscreenchange", handleFSChange);
    }, []);

    // Fullscreen logic
    const handleFullscreen = () => {
        const elem = document.documentElement;
        if (!document.fullscreenElement) {
            if (elem.requestFullscreen) {
                elem.requestFullscreen();
            } else if (elem.webkitRequestFullscreen) { /* Safari */
                elem.webkitRequestFullscreen();
            } else if (elem.msRequestFullscreen) { /* IE11 */
                elem.msRequestFullscreen();
            }
        } else {
            if (document.exitFullscreen) {
                document.exitFullscreen();
            } else if (document.webkitExitFullscreen) { /* Safari */
                document.webkitExitFullscreen();
            } else if (document.msExitFullscreen) { /* IE11 */
                document.msExitFullscreen();
            }
        }
    };

    const handleDownload = () => {
        if (!imageUrl) return;
        const a = document.createElement("a");
        a.href = imageUrl;
        a.download = fileName || "image.png";
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    const handleZoomIn = () => {
        zoomRef.current?.zoomIn();
    };
    const handleZoomOut = () => {
        zoomRef.current?.zoomOut();
    };

    useEffect(() => {
        if (!isVisible || !fo) {
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
                setImageUrl(null);
            }
            return;
        }

        const loadImage = async () => {
            setLoading(true);
            setError(null);
            // console.log("starting to load image with file object:", fo);
            let dataTask = null;

            try {
                const totSize = await fo.getSize();
                dataTask = new Task(totSize, fileName, "Viewing Image");

                const chunks = [];
                const chunkSize = 4 * 1024 * 1024; // 4MB
                let nextOffset = 0;
                let path = fo.getParentPath() + "/" + fo.getName();

                if (dupCurrentPath && dupCurrentPath.length === 1 && (dupCurrentPath[0] === "shared" || dupCurrentPath[0] === "external_shared")) {
                    fileName = fo.getFileUuId();
                    path = fo.getParentPath() + "/" + fileName;
                }

                while (nextOffset < totSize) {
                    const remainingChunkSize = Math.min(chunkSize, totSize - nextOffset);
                    const chunk = await fo.getChunk(nextOffset, path, dataTask, remainingChunkSize);
                    chunks.push(chunk);
                    nextOffset += chunkSize;
                }

                let blob = new Blob(chunks, { type: fo.type });
                // console.log(`Original file size: ${blob.size} bytes, MIME type: ${fo.type}`);
                const ext = fileName?.split(".").pop().toLowerCase();
                const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
                // console.log(`File extension: ${ext}, MIME type: ${fo.type}, Is Safari: ${isSafari}`);
                if ((ext === "heic" || ext === "heif" || fo.type === "image/heic") && !isSafari) {
                    // console.log("Converting HEIC image for browser compatibility...");
                    try {
                        const conversionResult = await heic2any({
                            blob,
                            toType: "image/jpeg",
                            quality: 0.8
                        });
                        blob = Array.isArray(conversionResult) ? conversionResult[0] : conversionResult;
                    } catch (convErr) {
                        console.error("HEIC conversion failed:", convErr);
                        throw new Error("Failed to convert HEIC image");
                    }
                }

                const url = URL.createObjectURL(blob);
                setImageUrl(url);
            } catch (err) {
                setError("Failed to load image");
                console.error("Failed to load image:", err);
            } finally {
                setLoading(false);
            }
        };

        loadImage();

        return () => {
            if (imageUrl) {
                URL.revokeObjectURL(imageUrl);
                setImageUrl(null);
            }
        };
    }, [isVisible, fo, fileName, retryTrigger]);

    const handleRetry = () => {
        setRetryTrigger(prev => prev + 1);
    };

    if (!isVisible) return null;

    return (
        <div className="image-viewer-container" data-theme={theme}>
            <div className="image-viewer-layout" data-theme={theme}>
                {/* Header - Always visible when modal is open */}
                <div className="image-viewer-header">
                    <div className="header-left">
                        <img src={theme === "dark" ? logoDark : logo} alt="Induz" className="header-logo" />
                    </div>
                    <div className="header-center">
                        <span className="file-name">{fileName}</span>
                    </div>
                    <div className="header-right">
                        {!error && !loading && imageUrl && (
                            <>
                                <button className="toolbar-btn" onClick={handleZoomOut} title="Zoom Out">
                                    <MinusIcon />
                                </button>
                                <button className="toolbar-btn" onClick={handleZoomIn} title="Zoom In">
                                    <PlusIcon />
                                </button>
                                <button className="toolbar-btn" onClick={handleDownload} title="Download">
                                    <img src={theme === "dark" ? downloadIconDark : downloadIcon} alt="Download" />
                                </button>
                                <button className="toolbar-btn" onClick={handleFullscreen} title={isFullscreen ? "Exit Fullscreen" : "Fullscreen"}>
                                    {isFullscreen ? <ExitFullScreenIcon /> : <EnterFullScreenIcon />}
                                </button>
                            </>
                        )}
                        <div className="close-btn" onClick={handleCancel}>
                            <CloseIcon />
                        </div>
                    </div>
                </div>

                {/* Content Area */}
                <div className="image-viewer-content">
                    {loading && (
                        <div className="loading-overlay">
                            <div className="spinner"></div>
                            <p>Loading Image...</p>
                        </div>
                    )}

                    {!loading && error && (
                        <div className="error-overlay">
                            <div className="error-card">
                                <div className="error-icon">
                                    <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="#FF4D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M12 8V12" stroke="#FF4D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                        <path d="M12 16H12.01" stroke="#FF4D4D" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    </svg>
                                </div>
                                <h3>Oops! Something went wrong</h3>
                                <p>We couldn`&apos;`t load the requested image. This might be due to a network issue or the file might be corrupted.</p>

                                <div className="technical-details">
                                    <details>
                                        <summary>Technical Details</summary>
                                        <div className="error-text">{error}</div>
                                    </details>
                                </div>

                                <div className="error-actions">
                                    <button className="error-retry-btn" onClick={handleRetry}>Retry</button>
                                    <button className="error-close-btn" onClick={handleCancel}>Exit Viewer</button>
                                </div>
                            </div>
                        </div>
                    )}

                    {!loading && !error && imageUrl && (
                        <Lightbox
                            open={isVisible}
                            close={handleCancel}
                            slides={[{ src: imageUrl }]}
                            plugins={[Inline, Zoom, FullScreen, Download]}
                            inline={{
                                style: { width: "100%", height: "100%" },
                            }}
                            zoom={{
                                ref: zoomRef,
                                maxZoomPixelRatio: 50,
                                scrollToZoom: true,
                                minZoom: 0.1,
                            }}
                            fullScreen={{
                                ref: fullScreenRef,
                            }}
                            render={{
                                buttonPrev: () => null,
                                buttonNext: () => null,
                                iconClose: () => null, // We act externally
                                iconZoomIn: () => null,
                                iconZoomOut: () => null,
                            }}
                            controller={{ closeOnBackdropClick: false }}
                            className="ysafe-lightbox"
                        />
                    )}
                </div>
            </div>
        </div>
    );
};

export default ImageViewer;
